import { useMemo } from "react";
import { Merchant } from "@/dto/merchant";
import { useMerchant } from "@/app/providers/merchantProvider";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { getAccessibleButtonStyles } from "../colorUtils";
import { DEFAULT_DUDA_PAGE_BG_COLOR } from "../constants";
import { isDudaWebsite } from "../dudaUtils";

const DUDA_VAR_REGEX = /var\(--(color[_-]\d+)\)/;

export const resolveDudaColor = (
  colorStr?: string | null,
  colorsPalette?: Array<{ id: string; value: string | null }> | null
): string | null => {
  if (!colorStr) return null;

  const match = DUDA_VAR_REGEX.exec(colorStr);

  if (match?.[1] && Array.isArray(colorsPalette)) {
    const key = match[1];
    const keyUnderscore = key.replace("-", "_");
    const keyHyphen = key.replace("_", "-");

    const found = colorsPalette.find(
      (c) => c.id === key || c.id === keyUnderscore || c.id === keyHyphen
    );

    return found?.value || null;
  }

  return colorStr;
};

export const getMerchantColor = ({
  merchant,
  preferredColor,
}: {
  merchant?: Merchant | null;
  preferredColor?: string | null;
}): string => {
  const isDuda = isDudaWebsite(merchant as Merchant);
  let dudaButtonColor: string | null = null;
  if (isDuda) {
    const targetColor =
      preferredColor ||
      merchant?.dudaWebsiteData?.theme?.buttons?.primary?.background?.color;
    const colorPalette = merchant?.dudaWebsiteData?.theme?.colors;

    dudaButtonColor = resolveDudaColor(targetColor, colorPalette);
    const accessibleButtonStyles = getAccessibleButtonStyles(
      DEFAULT_DUDA_PAGE_BG_COLOR,
      dudaButtonColor as string,
      ""
    );

    dudaButtonColor = accessibleButtonStyles?.buttonBgColor || dudaButtonColor;
  }

  return (
    dudaButtonColor ||
    merchant?.merchantMeta?.colorPalette?.color ||
    cloverColorTheme.Main.primaryColor
  );
};

export const useMerchantColor = ({
  preferredColor,
  merchant,
}: {
  preferredColor?: string | null;
  merchant?: Merchant | null;
} = {}): string => {
  const { merchant: contextMerchant } = useMerchant();
  merchant = merchant ?? contextMerchant;

  return useMemo(
    () => getMerchantColor({ merchant, preferredColor }),
    [merchant, preferredColor]
  );
};
